import { useState, useEffect, useRef } from 'react';
import { fetchGames } from '../api/gamesApi';
import { normalizeGasResponse } from '../utils/normalizeData';
import { sortByExpiry } from '../utils/sortGames';

const LS_CACHE_KEY = 'gamedrop_cache_v3';

function readClientCache() {
  try {
    const raw = localStorage.getItem(LS_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.games)) return null;
    return parsed; // { games, isDemoMode, dataFetchedAt }
  } catch {
    return null;
  }
}

function writeClientCache(games, isDemoMode, dataFetchedAt) {
  try {
    localStorage.setItem(LS_CACHE_KEY, JSON.stringify({
      games,
      isDemoMode:    isDemoMode || false,
      dataFetchedAt: dataFetchedAt || null,
    }));
  } catch { /* storage full — ignore */ }
}

export function useGames() {
  const cached = readClientCache();

  // Serve from localStorage immediately — instant display, no spinner if we have data
  const [games, setGames]                         = useState(cached?.games || []);
  const [loading, setLoading]                     = useState(!cached);
  const [backgroundRefresh, setBackgroundRefresh] = useState(false);
  const [error, setError]                         = useState(null);
  const [lastUpdated, setLastUpdated]             = useState(
    cached?.dataFetchedAt ? new Date(cached.dataFetchedAt) : null
  );
  const [isDemoMode, setIsDemoMode]               = useState(cached?.isDemoMode || false);

  const revalidated = useRef(false);

  useEffect(() => {
    if (revalidated.current) return;
    revalidated.current = true;

    const gasUrl  = process.env.REACT_APP_GAS_URL;
    const hasCache = !!cached;

    // GAS serves exclusively from its own cache (CacheService/PropertiesService) and
    // NEVER calls external APIs on user requests, so fetching it on every page load is
    // cheap. We always revalidate in the background so the client stays in sync with
    // whatever the GAS 12h scheduled refresh last produced.
    //
    // If localStorage is empty: show skeleton → fetch → display.
    // If localStorage has data: show it instantly → fetch GAS in background →
    //   update only if GAS dataFetchedAt is newer than what we already have.
    if (!hasCache) setLoading(true);
    else setBackgroundRefresh(true);

    async function fetchAndCache() {
      try {
        const { games: rawGames, isDemoMode: demo, updatedAt, stale } = await fetchGames(gasUrl);

        // GAS cache is empty (cleared before scheduledRefresh ran, or fresh deploy).
        // Keep showing whatever localStorage has — don't blank the screen.
        if (stale) {
          setLoading(false);
          setBackgroundRefresh(false);
          return;
        }

        const normalized = demo ? rawGames : normalizeGasResponse(rawGames);
        const sorted     = sortByExpiry(normalized);

        const newFetchedAt    = updatedAt || null;
        const cachedFetchedAt = cached?.dataFetchedAt || null;

        // Always update UI state so display reflects GAS truth.
        // Skip localStorage write only if the GAS snapshot hasn't changed
        // (same fetchedAt timestamp → same data → no point re-writing).
        const sameSnapshot = newFetchedAt && cachedFetchedAt && newFetchedAt === cachedFetchedAt;

        setGames(sorted);
        setIsDemoMode(demo);
        setLastUpdated(newFetchedAt ? new Date(newFetchedAt) : new Date());

        if (!demo && !sameSnapshot) {
          writeClientCache(sorted, demo, newFetchedAt);
        }
      } catch (err) {
        // On background refresh failure keep existing data visible — don't blank the screen.
        if (!hasCache) setError(err.message);
      } finally {
        setLoading(false);
        setBackgroundRefresh(false);
      }
    }

    fetchAndCache();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return { games, loading, backgroundRefresh, error, lastUpdated, isDemoMode };
}
